import { Box, Button, Card, Typography, Skeleton, Stack } from "@mui/material";
import HeaderAppBar from "./appbar";
import { AuthContext, BackendBaseUrl, ORANGE, BLACK, BG_LIGHT, CARD_SHADOW, PREMIUM_SHADOW } from "./global_varibles";
import { useContext, useEffect, useState } from "react";
import type { Dispatch, SetStateAction, Context } from "react";
import { Bell, BellOff, Phone, Calendar, CheckCircle2 } from "lucide-react";
import { alpha } from "@mui/material/styles";

type Notification = {
  id: number;
  name: string;
  phone_num: string;
  date: string;
};

interface GenerateNotificationCardsProps {
  notific_data: Notification[];
  req_id: number;
  setNotificationsData: Dispatch<SetStateAction<Notification[]>>;
}

function GenerateNoticationCards({ notific_data, req_id, setNotificationsData }: GenerateNotificationCardsProps) {
  const HandleMarkAsUnRead = async (req_id: number, id: number) => {
    try {
      const bkroute = `${BackendBaseUrl}mng-notifications`;
      const response = await fetch(bkroute, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ req_id, id, status: true }),
      });
      const response_data = (await response.json()) as { status?: string; message?: string };
      if (response.ok) {
        setNotificationsData((prevData) => prevData.filter((notific) => notific.id !== id));
      } else {
        console.log("error in updating the notifications", response_data?.message);
      }
    } catch (error) {
      console.log("error", error);
    }
  };

  if (notific_data.length === 0) {
    return (
      <Box 
        sx={{ 
          display: "flex", 
          flexDirection: "column",
          justifyContent: "center", 
          alignItems: "center", 
          height: "60vh",
          gap: 2
        }}
      >
        <Box 
          sx={{ 
            width: 80, 
            height: 80, 
            backgroundColor: alpha(ORANGE, 0.1), 
            borderRadius: "50%", 
            display: "flex", 
            alignItems: "center", 
            justifyContent: "center" 
          }}
        >
          <BellOff size={40} color={ORANGE} />
        </Box>
        <Typography variant="h5" sx={{ fontWeight: 800, color: BLACK }}>No Notifications</Typography>
        <Typography variant="body1" sx={{ color: "#64748b", fontWeight: 500 }}>You're all caught up!</Typography>
      </Box>
    );
  }

  return (
    <Box 
      sx={{ 
        width: "100%", 
        maxWidth: 800, 
        mx: "auto", 
        py: 4, 
        px: 2,
        display: "flex", 
        flexDirection: "column", 
        gap: 3 
      }}
    >
      <Box sx={{ mb: 2, textAlign: 'center' }}>
        <Typography variant="h4" sx={{ fontWeight: 900, color: BLACK, mb: 1 }}>Notifications</Typography>
        <Box sx={{ width: 40, height: 4, bgcolor: ORANGE, mx: 'auto', borderRadius: 2 }} />
      </Box>

      {notific_data.map((notific) => (
        <Card
          key={notific.id}
          sx={{
            width: "100%",
            backgroundColor: "#fff",
            borderRadius: 4,
            border: `1px solid ${alpha(ORANGE, 0.08)}`,
            boxShadow: CARD_SHADOW,
            transition: "all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
            "&:hover": { 
              transform: "translateY(-4px)",
              boxShadow: PREMIUM_SHADOW,
              borderColor: alpha(ORANGE, 0.3)
            },
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          <Box sx={{ position: 'absolute', top: 0, left: 0, width: 4, height: '100%', bgcolor: ORANGE }} />
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ width: 48, height: 48, bgcolor: alpha(ORANGE, 0.1), borderRadius: 3, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Bell size={24} color={ORANGE} />
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: BLACK, lineHeight: 1.2 }}>
                    {notific.name}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    New Message
                  </Typography>
                </Box>
              </Box>
              <Button 
                onClick={() => HandleMarkAsUnRead(req_id, notific.id)} 
                variant="contained"
                startIcon={<CheckCircle2 size={16} />}
                sx={{ 
                  bgcolor: alpha(ORANGE, 0.1), 
                  color: ORANGE,
                  fontWeight: 700,
                  textTransform: 'none',
                  borderRadius: 2,
                  boxShadow: 'none',
                  '&:hover': { bgcolor: ORANGE, color: '#fff' }
                }}
              >
                Done
              </Button>
            </Box>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Phone size={16} color="#94a3b8" />
                <Typography variant="body2" sx={{ color: "#475569", fontWeight: 600 }}>
                  {notific.phone_num}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Calendar size={16} color="#94a3b8" />
                <Typography variant="body2" sx={{ color: "#475569", fontWeight: 600 }}>
                  {new Date(notific.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                </Typography>
              </Box>
            </Stack>
          </Box>
        </Card>
      ))}
    </Box>
  );
}

interface AuthContextType {
  isAuthinticated: boolean;
  user_data: { id: number };
}

function ShowNotications() {
  const { isAuthinticated, user_data } = useContext(AuthContext as unknown as Context<AuthContextType>);
  const [loading, setLoading] = useState<boolean>(true);
  const [notificdata, setNotificationsData] = useState<Notification[]>([]);

  useEffect(() => {
    if (!isAuthinticated) return;

    const fetchNotificationsData = async () => {
      try {
        const backend_url = `${BackendBaseUrl}/mng-notifications?req_id=${user_data.id}`;
        const response = await fetch(backend_url, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        });
        const response_data = (await response.json()) as {
          status: "success" | "error";
          notific_data?: Notification[];
          message?: string;
        };
        if (response_data.status === "success" && Array.isArray(response_data.notific_data)) {
          setNotificationsData(response_data.notific_data);
        } else {
          console.error("Error in fetching data");
        }
      } catch (error) {
        console.error("Error fetching notifications:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchNotificationsData();
  }, [isAuthinticated, user_data.id]);

  if (!isAuthinticated) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100vh", backgroundColor: BG_LIGHT }}>
        <Typography variant="h5" sx={{ fontWeight: 800, color: BLACK }}>Access Denied</Typography>
        <Typography sx={{ color: "#64748b" }}>Please login to view notifications.</Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', backgroundColor: BG_LIGHT }}>
        <HeaderAppBar />
        <Box sx={{ maxWidth: 800, mx: "auto", py: 8, px: 2 }}>
          <Stack spacing={3}>
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} variant="rounded" height={160} sx={{ borderRadius: 4, bgcolor: alpha(ORANGE, 0.05) }} />
            ))}
          </Stack>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: BG_LIGHT, position: 'relative' }}>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          opacity: 0.02,
          pointerEvents: 'none',
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='28' height='28' viewBox='0 0 28 28' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23000000'%3E%3Ccircle cx='1' cy='1' r='1'/%3E%3C/g%3E%3C/svg%3E")`,
        }}
      />
      <HeaderAppBar />
      <GenerateNoticationCards
        notific_data={notificdata}
        req_id={user_data.id}
        setNotificationsData={setNotificationsData}
      />
    </Box>
  );
}

export default ShowNotications;